// MainPage.tsx
import React, { useEffect } from "react";
import { alpha, Box, CssBaseline, useTheme } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store/store";
import {
  toggleLeftSection,
  toggleRightSection,
} from "../store/slices/sectionSlice";
import LeftSection from "../components/ui/LeftSection";
import RightSection from "../components/ui/RightSection";
import Chat from "../components/ui/Chat";
import Header from "../components/ui/Header";
import CustomInputField from "../components/ui/CustomInputField";
import Footer from "../components/ui/Footer";
import { useParams } from "react-router-dom";
import { setActiveChatGroup } from "../store/slices/chatHistorySlice";
import useWebSocketContext from "../hooks/useWebSocketContext";
import fullBg from "../assets/full_bg.jpeg";
import fullBgLight from "../assets/full-bg-light.png";

const useToggleSections = () => {
  const dispatch = useDispatch();
  const isLeftOpen = useSelector((state: RootState) => state.section.leftOpen);
  const isRightOpen = useSelector(
    (state: RootState) => state.section.rightOpen
  );

  const handleToggleLeft = () => {
    dispatch(toggleLeftSection());
  };

  const handleToggleRight = () => {
    dispatch(toggleRightSection());
  };

  return { isLeftOpen, isRightOpen, handleToggleLeft, handleToggleRight };
};

const MainPage: React.FC = () => {
  const { isLeftOpen, isRightOpen, handleToggleLeft, handleToggleRight } =
    useToggleSections();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { chatId } = useParams();
  const { switchToChat, isConnected, sendToWs, waitForConnection } =
    useWebSocketContext();

  //Background image switch
  const backgroundMode = useSelector((state: RootState) => state.background.mode);
  const customImageUrl = useSelector((state: RootState) => state.background.customImageUrl);

  // Preload background images to prevent flashing
  useEffect(() => {
    const preloadImages = () => {
      const darkBgImage = new Image();
      const lightBgImage = new Image();
      
      darkBgImage.src = fullBg;
      lightBgImage.src = fullBgLight;
      
      // Optional: Add load event listeners for debugging
      darkBgImage.onload = () => console.log("Dark background image preloaded");
      lightBgImage.onload = () => console.log("Light background image preloaded");
    };

    preloadImages();
  }, []);

  useEffect(() => {
    const initializeChat = async () => {
      if (chatId && isConnected) {
        try {
          console.log("Waiting for WebSocket connection...");
          await waitForConnection();

          console.log("WebSocket connected, getting chats: " + chatId);
          dispatch(setActiveChatGroup(chatId));

          // First switch to chat
          await switchToChat(chatId);

          // Then get chat messages
          sendToWs(
            JSON.stringify({
              method: "get_chat_messages",
              params: {
                chat_group_id: chatId,
              },
            })
          );
        } catch (error) {
          console.error("Error initializing chat:", error);
        }
      }
    };

    initializeChat();
  }, [
    chatId,
    dispatch,
    switchToChat,
    isConnected,
    sendToWs,
    waitForConnection,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        paddingX: "15px",
        overflowY: "hidden",
        position: "relative",
      }}
      style={{
        backgroundColor: theme.palette.background.default,        
        backgroundImage:
          backgroundMode === "default"
            ? theme.palette.mode === "dark"
              ? `url(${fullBg})`
              : `url(${fullBgLight})`
            : backgroundMode === "custom" && customImageUrl
            ? `url(${customImageUrl})`
            : "none",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        backgroundPosition: "center center",
      }}
    >
      <CssBaseline />
      <LeftSection isOpen={isLeftOpen} onToggleSection={handleToggleLeft} />
      <Box
        component="main"
        sx={{
          height: "100vh",
          width: "100%",
          paddingTop: "15px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          transition: "margin 0.3s ease",
          position: "relative",
        }}
      >
        <Box
          component="div"
          sx={{
            display: "flex",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderTopLeftRadius: "15px",
            borderTopRightRadius: "15px",
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
            width: "100%",
            transition: "all 0.3s ease",
            position: "relative",
            backgroundColor: backgroundMode === "default" 
              ? "transparent" 
              : theme.palette.background.paper,
            backdropFilter: backgroundMode === "default" ? "blur(5px)" : "none",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: -1,
              backgroundColor: backgroundMode === "default" 
                ? theme.palette.mode != "dark" ? "transparent" : alpha(theme.palette.background.paper, 0.1)
                : "transparent",
              backdropFilter: backgroundMode === "default" ? "blur(5px)" : "none",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: -1,
              backgroundColor: backgroundMode === "default" 
                ? alpha(theme.palette.background.default, 0.2)
                : "transparent",
              pointerEvents: "none",
            }
          }}
        >
          <Header />
          <Chat />
          <CustomInputField />
          {/* <Footer /> */}
        </Box>
      </Box>
      <RightSection isOpen={isRightOpen} onToggleSection={handleToggleRight} />
    </Box>
  );
};

export default MainPage;